// ─────────────────────────────────────────────────────────────────────────────
// Helpers de DESENHO (Canvas 2D) — opcionais
//
// O sistema de movimento não desenha nada por si (é engine-agnóstico). Estas
// funções são a "cola" pronta para a engine Canvas 2D deste projeto: uma para
// pássaros procedurais e uma para recortar o frame de um spritesheet de NPC.
// ─────────────────────────────────────────────────────────────────────────────

import { Bird, Eagle } from './BirdSpawnerManager';
import { SpriteFrame } from './directions';
import { AnimationConfig } from './schema';

/**
 * Desenha um pássaro procedural (silhueta) na posição de tela dele.
 * `ctx` já deve estar no espaço de tela (sem transform de mundo), ou você
 * aplica `ctx.translate(-viewportOffsetX, -viewportOffsetY)` antes.
 */
export function drawBird(
  ctx: CanvasRenderingContext2D,
  bird: Bird,
  /** Diz se há chão sob o ponto (x, y) — mesmo espaço de `bird.x/y`. Sem ele, a sombra sempre aparece. */
  hasGroundAt?: (x: number, y: number) => boolean,
): void {
  const angle = Math.atan2(bird.vy, bird.vx);
  const flap = Math.sin(bird.flap * Math.PI * 2);
  const span = 13 * bird.scale;
  const wingY = span * flap;
  const s = bird.scale;

  // Centro da sombra projetada, no mesmo espaço de `bird.x/y`.
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const shadowX = bird.x + 14 * s * cos - 28 * s * sin;
  const shadowY = bird.y + 14 * s * sin + 28 * s * cos;
  const showShadow = !hasGroundAt || hasGroundAt(shadowX, shadowY);

  ctx.save();
  ctx.translate(bird.x, bird.y);
  ctx.rotate(angle);

  // sombra projetada (deslocada — dá sensação de altitude); só sobre chão
  if (showShadow) {
    ctx.save();
    ctx.translate(14 * s, 28 * s);
    ctx.fillStyle = 'rgba(0,0,0,0.13)';
    birdPath(ctx, wingY, s);
    ctx.fill();
    ctx.restore();
  }

  // corpo
  ctx.fillStyle = 'rgba(28,30,36,0.9)';
  birdPath(ctx, wingY, s);
  ctx.fill();

  ctx.restore();
}

/**
 * Águia caçadora: silhueta maior, marrom, com cabeça clara e asas largas.
 * Carregando a presa, desenha o pássaro preso nas garras (pendurado).
 */
export function drawEagle(
  ctx: CanvasRenderingContext2D,
  eagle: Eagle,
  hasGroundAt?: (x: number, y: number) => boolean,
): void {
  const angle = Math.atan2(eagle.vy, eagle.vx || 0.0001);
  const s = eagle.scale;
  const flap = Math.sin(eagle.flap * Math.PI * 2);
  const wingY = 22 * s * (0.8 + 0.2 * flap); // planador: abre e fecha pouco
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const shX = eagle.x + 18 * s * cos - 40 * s * sin;
  const shY = eagle.y + 18 * s * sin + 40 * s * cos;

  ctx.save();
  ctx.translate(eagle.x, eagle.y);
  ctx.rotate(angle);

  if (!hasGroundAt || hasGroundAt(shX, shY)) {
    ctx.save();
    ctx.translate(18 * s, 40 * s);
    ctx.fillStyle = 'rgba(0,0,0,0.16)';
    eaglePath(ctx, wingY, s);
    ctx.fill();
    ctx.restore();
  }

  ctx.fillStyle = '#4a3220';
  eaglePath(ctx, wingY, s);
  ctx.fill();
  ctx.strokeStyle = 'rgba(20,12,6,0.6)';
  ctx.lineWidth = 0.8;
  ctx.stroke();
  // cauda branca (águia-de-cabeça-branca)
  ctx.fillStyle = '#ece6d6';
  ctx.beginPath();
  ctx.moveTo(-14 * s, 2.2 * s); ctx.lineTo(-19 * s, 5 * s); ctx.lineTo(-20 * s, 0); ctx.lineTo(-19 * s, -5 * s); ctx.lineTo(-14 * s, -2.2 * s);
  ctx.closePath(); ctx.fill();
  // cabeça branca + bico amarelo
  ctx.fillStyle = '#f1ece0';
  ctx.beginPath(); ctx.arc(11 * s, 0, 3.2 * s, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#e8b02a';
  ctx.beginPath(); ctx.moveTo(13.5 * s, -1.2 * s); ctx.lineTo(17 * s, 0.6 * s); ctx.lineTo(13.5 * s, 1.4 * s); ctx.closePath(); ctx.fill();

  ctx.restore();

  // presa pendurada nas garras: abaixo da águia na TELA, balançando e se debatendo
  if (eagle.prey) {
    ctx.save();
    ctx.translate(eagle.x - 2 * s, eagle.y + 11 * s);
    ctx.rotate(Math.PI / 2 + Math.sin(eagle.age * 9) * 0.35);
    ctx.fillStyle = 'rgba(28,30,36,0.92)';
    birdPath(ctx, 5 * Math.sin(eagle.age * 22), 0.75);
    ctx.fill();
    ctx.restore();
    // garras segurando
    ctx.strokeStyle = '#e8b02a';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(eagle.x - 1 * s, eagle.y + 2 * s);
    ctx.lineTo(eagle.x - 2 * s, eagle.y + 7 * s);
    ctx.stroke();
  }
}

function eaglePath(ctx: CanvasRenderingContext2D, W: number, s: number): void {
  // x = frente. Asas abertas perpendiculares, levemente para trás, com as
  // "pontas em dedos" (penas primárias) e cauda em leque.
  const wing = (k: 1 | -1) => {
    ctx.quadraticCurveTo(2 * s, k * W * 0.55, -1 * s, k * W);          // bordo de ataque
    ctx.lineTo(-4 * s, k * W * 0.9);
    ctx.lineTo(-3.5 * s, k * W * 0.78);
    ctx.lineTo(-7 * s, k * W * 0.8);
    ctx.lineTo(-6.5 * s, k * W * 0.66);
    ctx.lineTo(-10 * s, k * W * 0.62);
    ctx.quadraticCurveTo(-9 * s, k * W * 0.3, -6 * s, k * 2.5 * s);    // bordo de fuga
  };
  ctx.beginPath();
  ctx.moveTo(6 * s, 2 * s);
  wing(1);
  ctx.lineTo(-13 * s, 2.5 * s);
  ctx.lineTo(-19 * s, 5.5 * s);
  ctx.lineTo(-20 * s, 0);
  ctx.lineTo(-19 * s, -5.5 * s);
  ctx.lineTo(-13 * s, -2.5 * s);
  ctx.lineTo(-6 * s, -2.5 * s);
  // asa esquerda percorrida ao contrário
  ctx.quadraticCurveTo(-9 * s, -W * 0.3, -10 * s, -W * 0.62);
  ctx.lineTo(-6.5 * s, -W * 0.66);
  ctx.lineTo(-7 * s, -W * 0.8);
  ctx.lineTo(-3.5 * s, -W * 0.78);
  ctx.lineTo(-4 * s, -W * 0.9);
  ctx.lineTo(-1 * s, -W);
  ctx.quadraticCurveTo(2 * s, -W * 0.55, 6 * s, -2 * s);
  ctx.lineTo(10 * s, -2 * s);
  ctx.lineTo(10 * s, 2 * s);
  ctx.closePath();
}

function birdPath(ctx: CanvasRenderingContext2D, wingY: number, s: number): void {
  ctx.beginPath();
  ctx.moveTo(10 * s, 0);
  ctx.quadraticCurveTo(2 * s, wingY * 1.2, -8 * s, wingY);
  ctx.quadraticCurveTo(-3 * s, wingY * 0.5, -6 * s, 2 * s);
  ctx.lineTo(-12 * s, 4 * s);
  ctx.lineTo(-10 * s, 0);
  ctx.lineTo(-12 * s, -4 * s);
  ctx.lineTo(-6 * s, -2 * s);
  ctx.quadraticCurveTo(-3 * s, -wingY * 0.5, -8 * s, -wingY);
  ctx.quadraticCurveTo(2 * s, -wingY * 1.2, 10 * s, 0);
  ctx.closePath();
}

/**
 * Retângulo de origem (px) do frame de um NPC dentro do sheet carregado.
 * Combine com `MovementComponent.getFrame(cameraAngle)`:
 *
 *   const fr = comp.getFrame(rot);
 *   const src = spriteFrameRect(fr, anim, img.width, img.height);
 *   ctx.drawImage(img, src.sx, src.sy, src.sw, src.sh, drawX, drawY, drawW, drawH);
 */
export function spriteFrameRect(
  frame: SpriteFrame,
  anim: AnimationConfig,
  sheetW: number,
  sheetH: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const sw = sheetW / anim.cols;
  const sh = sheetH / anim.rows;
  return { sx: frame.col * sw, sy: frame.row * sh, sw, sh };
}
